/**
 * NumberResponse — numeric input with optional min/max bounds.
 *
 * Value is a JS `number`. Empty / invalid strings produce null internally
 * and disable submit. `inputMode: numeric` gives mobile users the number
 * keypad without forcing integer-only.
 */

import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import type { ResponseCoreProps } from './types';
import './responses.css';

interface Props extends ResponseCoreProps<number> {
  min?: number;
  max?: number;
  /** Number of decimal places allowed. Undefined = any. */
  step?: number | 'any';
  placeholder?: string;
  /** Prefix shown inside the input (e.g. "$"). Aesthetic only — not
   *  part of the value. */
  prefix?: string;
  /** Suffix shown inside the input (e.g. "yrs"). */
  suffix?: string;
}

export function NumberResponse({
  label,
  hideLabel,
  initialValue = null,
  onChange,
  onSubmit,
  disabled,
  autoFocus,
  hideSubmit,
  submitLabel = 'Send',
  helper,
  min,
  max,
  step,
  placeholder,
  prefix,
  suffix,
}: Props) {
  const [raw, setRaw] = useState<string>(
    initialValue === null || initialValue === undefined
      ? ''
      : String(initialValue),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const parsed = parseNumber(raw);
  const inRange =
    parsed !== null &&
    (min === undefined || parsed >= min) &&
    (max === undefined || parsed <= max);

  function update(next: string) {
    setRaw(next);
    const p = parseNumber(next);
    if (p !== null) onChange?.(p);
  }

  function commit(e?: FormEvent) {
    if (e) e.preventDefault();
    if (parsed === null || !inRange) return;
    onSubmit?.(parsed);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  }

  const id = `cf-num-${label.replace(/\s+/g, '-')}`;
  const labelEl = hideLabel ? (
    <label htmlFor={id} className="cf-visually-hidden">
      {label}
    </label>
  ) : (
    <label htmlFor={id} className="cf-response__label">
      {label}
    </label>
  );

  const helperText =
    helper ??
    (min !== undefined || max !== undefined ? rangeText(min, max) : undefined);

  return (
    <form className="cf-response cf-number-response" onSubmit={commit}>
      {labelEl}
      <div className="cf-number-response__field">
        {prefix && (
          <span className="cf-number-response__affix" aria-hidden="true">
            {prefix}
          </span>
        )}
        <input
          ref={inputRef}
          id={id}
          type="number"
          inputMode="numeric"
          className="cf-input cf-number-response__input"
          value={raw}
          placeholder={placeholder}
          min={min}
          max={max}
          step={step as number | undefined}
          disabled={disabled}
          onChange={(e) => update(e.target.value)}
          onKeyDown={onKeyDown}
          aria-invalid={raw.length > 0 && !inRange}
        />
        {suffix && (
          <span className="cf-number-response__affix" aria-hidden="true">
            {suffix}
          </span>
        )}
      </div>
      {helperText && <div className="cf-response__helper">{helperText}</div>}
      {!hideSubmit && (
        <div className="cf-response__actions">
          <button
            type="submit"
            className="cf-btn cf-btn--primary"
            disabled={disabled || parsed === null || !inRange}
          >
            {submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}

function parseNumber(s: string): number | null {
  if (s.trim().length === 0) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function rangeText(min: number | undefined, max: number | undefined): string {
  if (min !== undefined && max !== undefined) return `Between ${min} and ${max}`;
  if (min !== undefined) return `At least ${min}`;
  if (max !== undefined) return `At most ${max}`;
  return '';
}
